import React from "react";
import { Card, CardContent, CardMedia, Container, Grid, Typography, Box } from "@mui/material";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

// Define the type for a feature item
interface Feature {
  title: string;
  description: string;
  image: string;
  alt: string;
}

// FeatureCard Component
const FeatureCard: React.FC<Feature> = ({ title, description, image, alt }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -8 }}
      transition={{ duration: 0.4 }}
      style={{ height: "100%" }}
    >
      <Card
        className="glass-card"
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          backgroundColor: "rgba(30, 41, 59, 0.55)",
          backdropFilter: "blur(16px)",
          border: "1px solid rgba(255, 255, 255, 0.15)",
          borderRadius: "24px",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.35)",
          overflow: "hidden",
          transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor: "rgba(30, 41, 59, 0.85)",
            borderColor: "rgba(168, 85, 247, 0.6)",
            boxShadow: "0 18px 45px rgba(99, 102, 241, 0.45), 0 0 25px rgba(217, 70, 239, 0.3)",
            "& .feature-title": {
              color: "#c084fc",
            },
            "& .feature-desc": {
              color: "#f8fafc",
            },
          },
        }}
      >
        <CardMedia
          sx={{
            overflow: "hidden",
            padding: "20px 20px 0 20px",
            height: "240px",
          }}
          title={title}
        >
          <motion.img
            src={image}
            alt={alt}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              borderRadius: "16px",
            }}
            whileHover={{ scale: 1.06 }}
            transition={{ duration: 0.4 }}
          />
        </CardMedia>
        <CardContent sx={{ padding: "24px", flexGrow: 1, display: "flex", flexDirection: "column" }}>
          <Typography
            variant="h5"
            className="feature-title"
            sx={{
              fontSize: "1.2rem",
              fontWeight: 700,
              color: "#f8fafc",
              marginBottom: "12px",
              lineHeight: 1.4,
              transition: "color 0.3s ease",
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            className="feature-desc"
            sx={{
              color: "#cbd5e1",
              fontSize: "0.95rem",
              lineHeight: 1.6,
              transition: "color 0.3s ease",
            }}
          >
            {description}
          </Typography>
        </CardContent>
      </Card>

    </motion.div>
  );
};

// Define props for FeatureList component
interface FeatureListProps {
  title: string;
  subtitle?: string;
  features: Feature[];
}

// FeatureList Component
const FeatureList: React.FC<FeatureListProps> = ({
  title,
  subtitle,
  features,
}) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <Container maxWidth="lg" className="py-5">
      <Box>
        <Box className="text-center mb-5 position-relative" style={{ zIndex: 1 }}>
          <Typography
            variant="h3"
            sx={{
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #d946ef 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontWeight: 800,
              fontSize: { xs: "1.8rem", md: "2.5rem" },
              marginBottom: "12px",
              letterSpacing: "-0.5px",
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="h6"
              sx={{ color: "rgb(148, 163, 184)", fontSize: "1.1rem", maxWidth: "700px", margin: "0 auto" }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 50 }}
          transition={{ duration: 0.6 }}
          style={{ position: "relative", zIndex: 1 }}
        >
          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <FeatureCard {...feature} />
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Box>
    </Container>
  );
};


// Feature Data
const multiFaceSwapFeatures: Feature[] = [
  {
    title: "Family Face Swap – Swap Faces with Your Loved Ones!",
    description:
      "Turn your family photo into something fun by swapping faces with parents, siblings, or even kids! Imagine switching faces with your dad or looking like your grandmother—it’s a hilarious way to create unforgettable memories.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/family_face_swap.jpg",
    alt: "Swap faces with family members and create fun and unforgettable memories.",
  },
  {
    title: "Couple Face Swap – See Yourself in Your Partner’s Shoes!",
    description:
      "Curious to see how you and your partner would look if you swapped faces? Try the couple face swap feature and laugh at the amazing transformations! Perfect for anniversaries, pranks, or just having fun.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/couple_face_swap.jpg",
    alt: "See how you and your partner would look after swapping faces with SwapToon’s AI.",
  },
  {
    title: "Friends Group Face Swap – Mix Up the Fun!",
    description:
      "Take a group selfie and swap everyone’s faces! Whether it’s a college reunion, office party, or a casual hangout, multi-face swapping brings a whole new level of fun to your group pictures.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/friends_group_face_swap.jpg",
    alt: "Swap faces in a group photo and enjoy hilarious transformations with friends.",
  },
  {
    title: "Age Swap – See Yourself at Different Ages!",
    description:
      "Swap faces between younger and older family members to see how you’ll look in the future—or how your grandparents would look younger!",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/age_swap_face_swap.jpg",
    alt: "See yourself at different ages by swapping faces between younger and older family members.",
  },
  {
    title: "Celebrity Group Face Swap – Be in a Celebrity Squad!",
    description:
      "Ever wanted to be part of a famous celebrity group? Upload an image of your favorite celebrities and swap your face with theirs to become a part of the crew!",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/celebrity_group_face_swap.jpg",
    alt: "Join a celebrity squad by swapping your face with famous personalities.",
  },
  {
    title: "Funny Face Swap Filters – Add a Creative Twist!",
    description:
      "SwapToon’s funny face swap filters let you try weird and wacky transformations. Turn yourself into a cartoon, swap genders, or mix things up for a crazy new look!",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/funny_face_swap_filters.jpg",
    alt: "Try funny face swap filters, transform into cartoons, swap genders, and explore creative looks.",
  },
];

const generalFeatures: Feature[] = [
  {
    title: "Swap Faces with Friends & Family",
    description:
      "SwapToon lets you easily swap faces with friends or family, creating hilarious and memorable photos. Simply upload your photos, and SwapToon’s AI ensures seamless facial expressions and skin tones, resulting in fun and shareable moments.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/face_swap_with_friends_family.jpg",
    alt: "Swap faces with friends and family using SwapToon AI for seamless and fun photo edits.",
  },
  {
    title: "Try Celebrity & Character Face Swaps",
    description:
      "With SwapToon's AI-powered face swap, you can see yourself as a celebrity or fictional character. Experience the thrill of embodying different personas with just a few clicks, and share the amusing results with your friends.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/celebrity_character_face_swap.jpg",
    alt: "Transform into a celebrity or fictional character with SwapToon's AI face swap feature.",
  },
  {
    title: "Cartoon Face Swap – See Yourself as a Cartoon!",
    description:
      "Transform into a cartoon character with SwapToon’s cartoon face swap filter. The AI adds fun elements like big eyes and exaggerated expressions, creating a unique and animated version of yourself. Perfect for custom avatars and profile pictures.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/cartoon_face_swap.jpg",
    alt: "Turn yourself into a cartoon character with SwapToon's fun cartoon face swap filter.",
  },
  {
    title: "Create Memes & Funny Edits",
    description:
      "SwapToon's face swap feature is ideal for creating memes and funny edits. Swap faces with famous meme characters, add humorous captions, and create viral content that spreads joy and laughter among your friends and followers.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/create_memes_funny_edits.jpg",
    alt: "Create hilarious memes and funny edits by swapping faces with famous meme characters.",
  },
  {
    title: "Face Morphing & Transformations",
    description:
      "Blend faces smoothly with SwapToon's face morphing feature. Experiment with different facial features, merge two faces into one, or create entirely new looks. Perfect for exploring creative transformations and visual effects.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/face_morphing_transformations.jpg",
    alt: "Blend and morph faces seamlessly with SwapToon's AI-powered face transformation feature.",
  },
  {
    title: "Try Different Looks & Styles",
    description:
      "Experiment with different hairstyles, gender, or makeup using SwapToon's AI face swap. See yourself with various hair colors and styles, or explore gender swaps before making real changes. Fun for trying new fashion and beauty styles.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/try_different_looks_styles.jpg",
    alt: "Experiment with different hairstyles, gender swaps, and makeup styles using AI face swap.",
  },
];

const preweddingFeatures: Feature[] = [
  {
    title: "Traditional Royal Pre-Wedding Shoots",
    description:
      "Swap faces into grand royal palace settings, traditional Indian lehengas, and embroidered sherwanis effortlessly.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/pre-wed/pre_wed_1.jpg",
    alt: "Traditional royal pre-wedding photo couple face swap.",
  },
  {
    title: "Scenic Lake & Mountain Romance",
    description:
      "Capture dreamy lakeside couple portraits with majestic mountain backdrops and elegant flowing bridal gowns.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/pre-wed/pre_wed_6.jpg",
    alt: "Scenic mountain lake pre-wedding couple photoshoot face swap.",
  },
  {
    title: "Adventure & Destination Travel Shoots",
    description:
      "Visualize yourself in picturesque outdoor destinations, scenic hiking spots, and alpine lake landscapes.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/pre-wed/pre_wed_7.jpg",
    alt: "Destination adventure pre-wedding couple face swap.",
  },
  {
    title: "Cinematic Boat Ride Romance",
    description:
      "Transform your photos into moody, romantic boat ride scenes and cinematic vintage couple portraits.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/pre-wed/pre_wed_9.jpg",
    alt: "Cinematic lake boat ride pre-wedding face swap.",
  },
  {
    title: "Studio & Professional Shoot Concepts",
    description:
      "Preview luxury floral arches, formal tuxedos, and studio-grade lighting poses before your actual photoshoot.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/pre-wed/pre_wed_5.jpg",
    alt: "Studio floral arch pre-wedding photo photoshoot face swap.",
  },
  {
    title: "Golden Hour Terrace & Save-The-Date",
    description:
      "Create warm golden-hour terrace portraits and romantic evening announcement photos for your Save-the-Date cards.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/pre-wed/pre_wed_10.jpg",
    alt: "Golden hour terrace sunset pre-wedding save the date face swap.",
  },
];

export const MultiFaceSwapFeatures: React.FC = () => (
  <FeatureList
    title="What You Can Do with Multi-Face Swap?"
    features={multiFaceSwapFeatures}
  />
);

export const PreweddingFeatures: React.FC = () => (
  <FeatureList
    title="What You Can Do with Pre-Wedding Face Swap?"
    subtitle="Explore romantic themes, luxury wedding attires, and breathtaking pre-wedding concepts."
    features={preweddingFeatures}
  />
);

const babyshowerFeatures: Feature[] = [
  {
    title: "3D Cartoon & Animated Baby Shower",
    description:
      "Transform your baby shower photos into charming 3D cartoon avatars and animated family portraits with a single tap!",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/baby-shower/baby_shower_13.jpg",
    alt: "3D cartoon baby shower multi face swap with animated family avatars.",
  },
  {
    title: "Mom-to-Be Squad & Party Swaps",
    description:
      "Swap faces with your besties and family around the 'Oh Baby!' backdrop to capture unforgettable party moments.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/baby-shower/baby_shower_11.jpg",
    alt: "Mom to be sash baby shower celebration multi face swap with friends.",
  },
  {
    title: "Maternity & Couple Portrait Swaps",
    description:
      "Try luxury maternity gowns and elegant couple outfits in warm, festive teddy-bear themed baby shower setups.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/baby-shower/baby_shower_9.jpg",
    alt: "Maternity shoot couple portrait face swap with teddy bear theme.",
  },
  {
    title: "Family & Grandparents Group Swaps",
    description:
      "Include grandparents, siblings, and loved ones in beautiful multi-face group photos to celebrate the upcoming baby.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/baby-shower/baby_shower_8.jpg",
    alt: "Family and grandparents multi face swap for baby shower.",
  },
  {
    title: "Solo Maternity & Digital Invites",
    description:
      "Place yourself in dream pastel balloon arches and elegant maternity dresses for stunning solo portraits & invite cards.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/baby-shower/baby_shower_7.jpg",
    alt: "Solo pregnant mom face swap in pastel balloon arch setup.",
  },
  {
    title: "Fairy Tale & Teddy Bear Themes",
    description:
      "Step into magical fairy-light setups and adorable teddy-bear themes for whimsical, heartwarming baby shower photos.",
    image:
      "https://snaptoon-development.s3.eu-north-1.amazonaws.com/snaptoon_web/baby-shower/baby_shower_3.jpg",
    alt: "Fairy tale teddy bear baby shower couple face swap.",
  },
];

export const BabyshowerFeatures: React.FC = () => (
  <FeatureList
    title="What You Can Do with Baby Shower Face Swap?"
    subtitle="Create memorable maternity portraits, gender reveal photos, and festive baby shower group edits."
    features={babyshowerFeatures}
  />
);

export const Features: React.FC = () => (
  <FeatureList
    title="What You Can Do with SwapToon?"
    subtitle="SwapToon isn’t just a face swap app—it’s a creative tool that lets you have fun with your photos."
    features={generalFeatures}
  />
);
